import {
  MessageCircle,
  Phone,
  Mail,
  MapPin,
  ArrowRight,
} from "lucide-react";
import MY_CONFIG from "@/constants";

export default function ContactForm() {
  const emailAddress = MY_CONFIG.EMAIL;
  const phoneNumber = "9609423609";

  const whatsappUrl = `https://wa.me/${phoneNumber}`;
  const viberUrl = `viber://chat?number=${phoneNumber}`;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-10">

      {/* HEADER */}
      <div className="text-center mb-10">
        <h1 className="text-4xl md:text-5xl font-black">
          Contact & Support
        </h1>
        <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
          Choose your preferred way to reach us. We respond quickly on all channels.
        </p>
      </div>

      {/* GRID */}
      <div className="grid md:grid-cols-2 gap-6">

        {/* LEFT - INFO CARD */}
        <div className="rounded-3xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white p-6 md:p-8 shadow-xl">

          <h2 className="text-2xl font-bold mb-2">
            Get Instant Support
          </h2>

          <p className="text-white/80 mb-8">
            Contact us anytime via email, phone, or messaging apps.
          </p>

          <div className="space-y-4">

            <InfoRow icon={<Mail />} label="Email" value={emailAddress} />
            <InfoRow icon={<Phone />} label="Phone" value={`+${phoneNumber}`} />
            <InfoRow icon={<MapPin />} label="Location" value="Male’, Maldives" />

          </div>

          {/* PRIMARY CTA */}
          <div className="mt-8">
            <a
              href={`mailto:${emailAddress}`}
              className="flex items-center justify-center gap-2 bg-white text-blue-700 font-semibold py-3 rounded-2xl hover:bg-white/90 transition"
            >
              Send Email <ArrowRight size={18} />
            </a>
          </div>
        </div>

        {/* RIGHT - ACTION CARDS */}
        <div className="grid gap-4">

          <ActionCard
            title="WhatsApp Chat"
            desc="Fast response via WhatsApp"
            icon={<MessageCircle className="text-green-600" />}
            href={whatsappUrl}
            color="bg-green-500"
          />

          <ActionCard
            title="Viber Chat"
            desc="Message us on Viber"
            icon={<Phone className="text-purple-600" />}
            href={viberUrl}
            color="bg-purple-600"
          />

          <ActionCard
            title="Call Now"
            desc="Direct phone support"
            icon={<Phone className="text-black" />}
            href={`tel:${phoneNumber}`}
            color="bg-black"
          />

          <div className="rounded-3xl border bg-gray-50 p-6">
            <h3 className="font-semibold text-lg mb-2">
              Official Hours
            </h3>
            <p className="text-gray-600 text-sm">
              Sat - Thu: 8:00 AM – 16:00 PM
            </p>
          </div>

        </div>
      </div>
    </div>
  );
}

/* INFO ROW */
function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-3 bg-white/10 p-4 rounded-2xl">
      <div className="opacity-90">{icon}</div>
      <div>
        <p className="text-sm text-white/70">{label}</p>
        <p className="font-semibold">{value}</p>
      </div>
    </div>
  );
}

/* ACTION CARD */
function ActionCard({
  title,
  desc,
  icon,
  href,
  color,
}: {
  title: string;
  desc: string;
  icon: React.ReactNode;
  href: string;
  color: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      className="group flex items-center justify-between bg-white border rounded-3xl p-5 shadow-sm hover:shadow-md transition"
    >
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center">
          {icon}
        </div>

        <div>
          <h3 className="font-semibold">{title}</h3>
          <p className="text-sm text-gray-500">{desc}</p>
        </div>
      </div>

      <ArrowRight className="text-gray-400 group-hover:translate-x-1 transition" />
    </a>
  );
}